import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { Store } from '@ngxs/store';
import {
  SetPromoBannerVisibility,
  ShellState,
  UserPrefState,
} from './shell.state';
import {
  combineLatest,
  filter,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '../shared/services/analytics.service';
import { PageLoaderComponent } from './page-loader/page-loader.component';
import { TalkdeskWebchatComponent } from 'showtix-shared';
import { WWW_CONFIG } from '../shared/models/config.model';
import { NotificationBannerComponent } from './notification-banner/notification-banner.component';
import { PageSelectors } from '../page/state/page.selectors';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AdyenDataCollectionService } from '../shared/services/adyen-data-collection.service';
import { SegmentService } from 'segment';

@Component({
  selector: 'ss-www-shell',
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    PageLoaderComponent,
    TalkdeskWebchatComponent,
    NotificationBannerComponent,
  ],
  templateUrl: './shell.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
  host: {
    '(window:OneTrustGroupsUpdated)': 'handleConsentChange($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent implements OnInit, OnDestroy {
  router = inject(Router);
  destroy$ = new Subject<void>();
  private store = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private analyticsService = inject(AnalyticsService);
  private readonly segmentService = inject(SegmentService);
  private adyenDataCollectionService = inject(AdyenDataCollectionService);
  config = inject(WWW_CONFIG);
  isMobileDevice$ = inject(BreakpointObserver)
    .observe([Breakpoints.Handset])
    .pipe(
      map((result) => result.matches),
      take(2)
    )
    .pipe(map((isMobileDevice) => isMobileDevice));

  private userPrefDate$ = this.store.select(UserPrefState.dateUpdated);
  isTicketingPage$ = signal(false);
  private headerContent$ = this.store.select(ShellState.headerContent).pipe(
    map((headerContent) => {
      if (!headerContent?.date_updated) return Date.now();
      return Date.parse(headerContent?.date_updated + 'Z');
    })
  );
  private showBannerFlag$ = this.store
    .select(ShellState.headerContent)
    .pipe(map((headerContent) => headerContent?.showPromoBanner ?? false));
  isBannerVisible$ = combineLatest([
    this.userPrefDate$,
    this.headerContent$,
    this.showBannerFlag$,
  ]).pipe(
    take(1),
    switchMap(([userPrefDate, headerContent, showBannerFlag]) => {
      if (!isPlatformBrowser(this.platformId)) return of(false);
      return this.store.dispatch(
        new SetPromoBannerVisibility(
          headerContent >= userPrefDate && showBannerFlag
        )
      );
    }),
    switchMap(() => this.store.select(UserPrefState.isPromoBannerVisible))
  );
  private hiddenFooterPages = [
    /^\/ticketing$/,
    /^\/ticketing\/[a-zA-Z0-9]{5,}\/calendar$/,
    /^\/ticketing\/[a-zA-Z0-9]{5,}\/(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})$/,
    /^\/ticketing\/[a-zA-Z0-9]{5,}\/(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})\/seats$/,
    /^\/ticketing\/cart$/,
    /^\/ticketing\/checkout$/,
  ];

  showTalkDesk = toSignal(this.store.select(ShellState.showTalkDesk));
  isAppStable$ = inject(ApplicationRef).isStable.pipe(
    filter((isStable) => isStable && isPlatformBrowser(this.platformId)),
    take(1)
  );

  isAppStable = toSignal(this.isAppStable$);
  isPageHeaderHidden = this.store.selectSignal(PageSelectors.pageHeaderHidden);
  isPageFooterHidden = this.store.selectSignal(PageSelectors.pageFooterHidden);

  ngOnInit(): void {
    this.setIsTicketingPage(this.router.url);
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setIsTicketingPage(event.url);
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.isAppStable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.analyticsService.initConsent();
        this.adyenDataCollectionService.initializeAdyenDataCollection();
        this.segmentService.initializeSegment();
      });
    }
  }

  handleConsentChange(event: Event) {
    const customEvent = event as CustomEvent<string[]>;
    const consentStatus = {
      targeting: customEvent.detail.includes('C0004'),
      performance: customEvent.detail.includes('C0002'),
      functional: customEvent.detail.includes('C0003'),
      strictlyNecessary: customEvent.detail.includes('C0001'),
    };
    this.analyticsService.handleConsent(consentStatus);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setIsTicketingPage(url: string) {
    this.isTicketingPage$.set(
      this.hiddenFooterPages.some((page) => page.test(url))
    );
  }
}
